<template>
  <div class="evaluation-page">
    <div class="page-header">
      <h2 class="page-title">报告评价</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">报告年度</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="全部"
          clearable
          value-format="yyyy"
          style="width: 120px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">评价类型</span>
        <el-select v-model="evalType" size="small" clearable placeholder="全部" style="width: 140px">
          <el-option
            v-for="item in evalTypes"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">评价结果</span>
        <el-select v-model="result" size="small" clearable placeholder="全部" style="width: 140px">
          <el-option
            v-for="item in resultOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row stat-cards-3">
      <stat-card
        title="待评价"
        :today="summary.pending.today"
        :annual="summary.pending.annual"
        :unit="summary.pending.unit"
        :annual-unit="summary.pending.unit"
        icon="el-icon-time"
        color="#E6A23C"
      />
      <stat-card
        title="通过"
        :today="summary.passed.today"
        :annual="summary.passed.annual"
        :unit="summary.passed.unit"
        :annual-unit="summary.passed.unit"
        icon="el-icon-circle-check"
        color="#67C23A"
      />
      <stat-card
        title="需整改"
        :today="summary.rectify.today"
        :annual="summary.rectify.annual"
        :unit="summary.rectify.unit"
        :annual-unit="summary.rectify.unit"
        icon="el-icon-warning-outline"
        color="#F56C6C"
      />
    </div>

    <chart-card title="评价结果分布" class="chart-section">
      <base-chart :option="resultChartOption" />
    </chart-card>

    <div class="table-section">
      <div class="table-toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openEvalDialog(null, true)">
          发起评价
        </el-button>
      </div>
      <el-table :data="evaluations" border stripe size="small">
        <el-table-column prop="reportName" label="关联报告" min-width="220" show-overflow-tooltip />
        <el-table-column prop="reportYear" label="报告年度" width="100" align="center" />
        <el-table-column prop="evalTypeLabel" label="评价类型" width="120" />
        <el-table-column prop="evaluator" label="评价机构/人员" width="140" />
        <el-table-column prop="score" label="综合评分" width="100" align="center">
          <template slot-scope="{ row }">
            {{ row.score || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="resultLabel" label="结论" width="120" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="resultTagType(row.result)" size="mini">{{ row.resultLabel }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="evalDate" label="评价日期" width="120">
          <template slot-scope="{ row }">{{ row.evalDate || '-' }}</template>
        </el-table-column>
        <el-table-column prop="statusLabel" label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 'completed' ? 'success' : 'warning'" size="mini">
              {{ row.statusLabel }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openEvalDialog(row, false)">
              {{ row.status === 'pending' ? '评价' : '查看' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      :title="evalDialogTitle"
      :visible.sync="evalDialogVisible"
      width="560px"
      @close="resetEvalForm"
    >
      <el-form :model="evalForm" label-width="120px" size="small">
        <el-form-item v-if="isNewEval" label="关联报告" required>
          <el-select v-model="evalForm.reportId" placeholder="请选择报告" style="width: 100%">
            <el-option
              v-for="item in availableReports"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-else label="关联报告">
          <el-input :value="evalForm.reportName" disabled />
        </el-form-item>
        <el-form-item label="评价类型" required>
          <el-select
            v-model="evalForm.evalType"
            placeholder="请选择"
            style="width: 100%"
            :disabled="!isEditable"
          >
            <el-option
              v-for="item in evalTypes"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="评价机构/人员" required>
          <el-input v-model="evalForm.evaluator" :disabled="!isEditable" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="符合性检查">
          <el-checkbox-group v-model="evalForm.checklist" :disabled="!isEditable">
            <el-checkbox
              v-for="item in checklistOptions"
              :key="item"
              :label="item"
              style="display: block; margin-left: 0; margin-bottom: 4px"
            />
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="数据质量等级">
          <el-select
            v-model="evalForm.dataQuality"
            placeholder="请选择"
            style="width: 100%"
            :disabled="!isEditable"
          >
            <el-option v-for="item in dataQualityOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="综合评分">
          <el-input-number
            v-model="evalForm.score"
            :min="0"
            :max="100"
            :disabled="!isEditable"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="评价结论" required>
          <el-radio-group v-model="evalForm.conclusion" :disabled="!isEditable">
            <el-radio label="passed">通过</el-radio>
            <el-radio label="conditional">有条件通过</el-radio>
            <el-radio label="rectify">不通过/需整改</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="整改意见">
          <el-input
            v-model="evalForm.remarks"
            type="textarea"
            :rows="3"
            :disabled="!isEditable"
            placeholder="请输入评价意见或整改要求"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="evalDialogVisible = false">
          {{ isEditable ? '取消' : '关闭' }}
        </el-button>
        <el-button v-if="isEditable" type="primary" size="small" @click="saveEvaluation">提交评价</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getEvaluationData } from '@/api/modules/carbonAccounting'

const RESULT_LABELS = {
  passed: '通过',
  conditional: '有条件通过',
  rectify: '需整改',
  pending: '待评价'
}

const EVAL_TYPE_LABELS = {
  internal: '内部评审',
  third_party: '第三方核查'
}

export default {
  name: 'CarbonAccountingEvaluation',
  components: { FilterBar, StatCard, ChartCard, BaseChart },
  data() {
    return {
      year: '',
      evalType: '',
      result: '',
      evalTypes: [],
      resultOptions: [],
      checklistOptions: [],
      dataQualityOptions: [],
      availableReports: [],
      summary: {
        pending: { today: 0, annual: 0, unit: '份' },
        passed: { today: 0, annual: 0, unit: '份' },
        rectify: { today: 0, annual: 0, unit: '份' }
      },
      evaluations: [],
      resultChartOption: {},
      evalDialogVisible: false,
      evalDialogTitle: '发起评价',
      isNewEval: false,
      isEditable: true,
      evalForm: {
        id: null,
        reportId: null,
        reportName: '',
        evalType: 'internal',
        evaluator: '',
        checklist: [],
        dataQuality: '',
        score: 0,
        conclusion: 'passed',
        remarks: ''
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      const res = await getEvaluationData({
        year: this.year,
        evalType: this.evalType,
        result: this.result
      })
      const data = res.data
      this.evalTypes = data.evalTypes
      this.resultOptions = data.resultOptions
      this.checklistOptions = data.checklistOptions
      this.dataQualityOptions = data.dataQualityOptions
      this.availableReports = data.availableReports
      this.summary = data.summary
      this.evaluations = data.evaluations
      this.buildResultChart(data.resultDist)
    },
    buildResultChart(dist) {
      this.resultChartOption = {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { orient: 'vertical', right: 10, top: 'center' },
        color: ['#67C23A', '#409EFF', '#F56C6C', '#E6A23C'],
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['40%', '50%'],
          data: dist,
          label: { formatter: '{b}\n{d}%' }
        }]
      }
    },
    resultTagType(result) {
      const map = {
        passed: 'success',
        conditional: 'warning',
        rectify: 'danger',
        pending: 'info'
      }
      return map[result] || 'info'
    },
    openEvalDialog(row, isNew) {
      this.isNewEval = isNew
      if (isNew) {
        this.evalDialogTitle = '发起评价'
        this.isEditable = true
        this.resetEvalForm()
      } else if (row) {
        this.evalDialogTitle = row.status === 'pending' ? '评价报告' : '查看评价'
        this.isEditable = row.status === 'pending'
        this.evalForm = {
          id: row.id,
          reportId: null,
          reportName: row.reportName,
          evalType: row.evalType,
          evaluator: row.evaluator,
          checklist: [...row.checklist],
          dataQuality: row.dataQuality,
          score: row.score,
          conclusion: row.conclusion || 'passed',
          remarks: row.remarks
        }
      }
      this.evalDialogVisible = true
    },
    resetEvalForm() {
      this.evalForm = {
        id: null,
        reportId: null,
        reportName: '',
        evalType: 'internal',
        evaluator: '',
        checklist: [],
        dataQuality: '',
        score: 0,
        conclusion: 'passed',
        remarks: ''
      }
    },
    saveEvaluation() {
      const report = this.availableReports.find(r => r.id === this.evalForm.reportId)
      const evalTypeLabel = EVAL_TYPE_LABELS[this.evalForm.evalType]
      const resultLabel = RESULT_LABELS[this.evalForm.conclusion]

      if (this.isNewEval && report) {
        this.evaluations.unshift({
          id: Date.now(),
          reportName: report.name,
          reportYear: 2025,
          evalType: this.evalForm.evalType,
          evalTypeLabel,
          evaluator: this.evalForm.evaluator,
          score: this.evalForm.score,
          result: this.evalForm.conclusion,
          resultLabel,
          evalDate: new Date().toISOString().slice(0, 10),
          status: 'completed',
          statusLabel: '已完成',
          checklist: [...this.evalForm.checklist],
          dataQuality: this.evalForm.dataQuality,
          conclusion: this.evalForm.conclusion,
          remarks: this.evalForm.remarks
        })
      } else if (this.evalForm.id) {
        const idx = this.evaluations.findIndex(e => e.id === this.evalForm.id)
        if (idx >= 0) {
          this.$set(this.evaluations, idx, {
            ...this.evaluations[idx],
            evalType: this.evalForm.evalType,
            evalTypeLabel,
            evaluator: this.evalForm.evaluator,
            score: this.evalForm.score,
            result: this.evalForm.conclusion,
            resultLabel,
            evalDate: new Date().toISOString().slice(0, 10),
            status: 'completed',
            statusLabel: '已完成',
            checklist: [...this.evalForm.checklist],
            dataQuality: this.evalForm.dataQuality,
            conclusion: this.evalForm.conclusion,
            remarks: this.evalForm.remarks
          })
        }
      }
      this.evalDialogVisible = false
      this.$message.success('评价已提交')
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-header {
  margin-bottom: 16px;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.chart-section {
  height: 320px;
  margin-bottom: 16px;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-toolbar {
  margin-bottom: 12px;
}
</style>
